/**
 * Date Utilities Library
 *
 * Centralized date handling functions for consistent date operations across the application.
 * Provides type-safe wrappers around date-fns with Brazilian locale support.
 *
 * @module lib/utils/dateUtils
 */

import {
  format,
  parse,
  isValid,
  addDays as dateFnsAddDays,
  differenceInDays,
  startOfDay,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';
import { PeriodType } from '@/hooks/useTransactionFilters';

/**
 * Converts a Date object to ISO date string format (YYYY-MM-DD)
 *
 * @param date - Date object or ISO date string
 * @returns ISO date string (YYYY-MM-DD)
 * @throws Error if date is invalid
 *
 * @example
 * toDateString(new Date('2024-01-15')) // '2024-01-15'
 * toDateString('2024-01-15T10:30:00') // '2024-01-15'
 */
export function toDateString(date: Date | string): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = parse(date, 'yyyy-MM-dd', new Date());
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return format(dateObj, 'yyyy-MM-dd');
}

/**
 * Parses a date string (YYYY-MM-DD or ISO format) to Date object
 *
 * @param date - Date object or date string in YYYY-MM-DD or ISO format
 * @returns Date object
 * @throws Error if string cannot be parsed to valid date
 *
 * @example
 * fromDateString('2024-01-15') // Date object for 2024-01-15
 * fromDateString('2024-01-15T10:30:00Z') // Date object for 2024-01-15 10:30
 * fromDateString(new Date()) // Returns the same Date object
 */
export function fromDateString(date: Date | string): Date {
  // If it's already a Date object, validate and return it
  if (date instanceof Date) {
    if (!isValid(date)) {
      throw new Error(`Invalid date object: ${date}`);
    }
    return date;
  }

  // If it's null or undefined, throw error
  if (!date) {
    throw new Error(`Cannot parse date: ${date}`);
  }

  // Try parsing as ISO date (YYYY-MM-DD)
  const parsed = parse(date, 'yyyy-MM-dd', new Date());

  if (isValid(parsed)) {
    return parsed;
  }

  // Try parsing as full ISO string
  const isoDate = new Date(date);

  if (isValid(isoDate)) {
    return isoDate;
  }

  throw new Error(`Cannot parse date string: ${date}`);
}

/**
 * Formats a Date object to Brazilian format (dd/MM/yyyy)
 *
 * IMPORTANT: This function handles timezone-safe parsing of date strings.
 * When passing date strings from the database (YYYY-MM-DD format),
 * this function will parse them as local dates to avoid UTC conversion issues.
 *
 * @param date - Date object or ISO date string
 * @returns Formatted string in dd/MM/yyyy format
 * @throws Error if date is invalid
 *
 * @example
 * formatBR(new Date('2024-01-15')) // '15/01/2024'
 * formatBR('2024-01-15') // '15/01/2024' - timezone safe
 * formatBR('2024-01-15T10:30:00Z') // '15/01/2024' - handles ISO with timezone
 */
export function formatBR(date: Date | string): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone issues
    // This prevents dates like "2025-11-14" from becoming "13/11/2025" due to UTC conversion
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = parse(date, 'yyyy-MM-dd', new Date());
    } else if (date.includes('T')) {
      // For ISO strings with time, parse as UTC to avoid timezone shifts
      dateObj = new Date(date);
      // Check if the date was parsed as UTC and needs adjustment
      const utcStr = dateObj.toISOString();
      if (utcStr.startsWith(date.substring(0, 10))) {
        // If it's a date-only string being treated as UTC, parse as local
        dateObj = parse(date.substring(0, 10), 'yyyy-MM-dd', new Date());
      }
    } else {
      // Fallback for other string formats
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return format(dateObj, 'dd/MM/yyyy', { locale: ptBR });
}

/**
 * Formats a Date object to Brazilian long format (dd 'de' MMMM)
 *
 * @param date - Date object or ISO date string
 * @returns Formatted string like "15 de janeiro"
 * @throws Error if date is invalid
 *
 * @example
 * formatBRLong(new Date('2024-01-15')) // '15 de janeiro'
 */
export function formatBRLong(date: Date | string): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = parse(date, 'yyyy-MM-dd', new Date());
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return format(dateObj, "dd 'de' MMMM", { locale: ptBR });
}

/**
 * Formats a Date object to Brazilian formal long format (dd 'de' MMMM 'de' yyyy)
 *
 * @param date - Date object or ISO date string
 * @returns Formatted string like "15 de janeiro de 2024"
 * @throws Error if date is invalid
 *
 * @example
 * formatDateBR(new Date('2024-01-15')) // '15 de janeiro de 2024'
 * formatDateBR('2025-12-03') // '3 de dezembro de 2025'
 */
export function formatDateBR(date: Date | string): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = parse(date, 'yyyy-MM-dd', new Date());
    } else if (date.includes('T')) {
      // For ISO strings with time, parse as UTC to avoid timezone shifts
      dateObj = new Date(date);
      // Check if the date was parsed as UTC and needs adjustment
      const utcStr = dateObj.toISOString();
      if (utcStr.startsWith(date.substring(0, 10))) {
        // If it's a date-only string being treated as UTC, parse as local
        dateObj = parse(date.substring(0, 10), 'yyyy-MM-dd', new Date());
      }
    } else {
      // Fallback for other string formats
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return format(dateObj, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

/**
 * Formats a Date object to short Brazilian format (dd/MM/yy)
 *
 * @param date - Date object or ISO date string
 * @returns Formatted string in dd/MM/yy format
 * @throws Error if date is invalid
 *
 * @example
 * formatBRShort(new Date('2024-01-15')) // '15/01/24'
 */
export function formatBRShort(date: Date | string): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = parse(date, 'yyyy-MM-dd', new Date());
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return format(dateObj, 'dd/MM/yy', { locale: ptBR });
}

/**
 * Validates if a value is a valid date
 *
 * @param date - Value to validate (can be any type)
 * @returns true if valid Date object or parseable date string, false otherwise
 *
 * @example
 * isValidDate(new Date()) // true
 * isValidDate('2024-01-15') // true
 * isValidDate('invalid') // false
 * isValidDate(null) // false
 * isValidDate(undefined) // false
 */
export function isValidDate(date: any): boolean {
  if (date == null) return false;

  if (date instanceof Date) {
    return isValid(date);
  }

  if (typeof date === 'string') {
    const parsed = new Date(date);
    return isValid(parsed);
  }

  return false;
}

/**
 * Adds specified number of days to a date
 *
 * @param date - Base date
 * @param days - Number of days to add (can be negative to subtract)
 * @returns New Date object with added days
 * @throws Error if date is invalid
 *
 * @example
 * addDays(new Date('2024-01-15'), 5) // 2024-01-20
 * addDays(new Date('2024-01-15'), -3) // 2024-01-12
 */
export function addDays(date: Date | string, days: number): Date {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = parse(date, 'yyyy-MM-dd', new Date());
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return dateFnsAddDays(dateObj, days);
}

/**
 * Calculates the difference in days between two dates
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Number of days between dates (positive if date1 > date2)
 * @throws Error if either date is invalid
 *
 * @example
 * diffDays(new Date('2024-01-20'), new Date('2024-01-15')) // 5
 * diffDays(new Date('2024-01-15'), new Date('2024-01-20')) // -5
 */
export function diffDays(date1: Date | string, date2: Date | string): number {
  const dateObj1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const dateObj2 = typeof date2 === 'string' ? new Date(date2) : date2;

  if (!isValid(dateObj1)) {
    throw new Error(`Invalid date1: ${date1}`);
  }

  if (!isValid(dateObj2)) {
    throw new Error(`Invalid date2: ${date2}`);
  }

  return differenceInDays(dateObj1, dateObj2);
}

/**
 * Gets the start of day (00:00:00.000) for a given date
 *
 * @param date - Date to get start of day for
 * @returns Date object at start of day
 * @throws Error if date is invalid
 *
 * @example
 * getStartOfDay(new Date('2024-01-15 15:30:00')) // 2024-01-15 00:00:00
 */
export function getStartOfDay(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return startOfDay(dateObj);
}

/**
 * Gets the end of day (23:59:59.999) for a given date
 *
 * @param date - Date to get end of day for
 * @returns Date object at end of day
 * @throws Error if date is invalid
 *
 * @example
 * getEndOfDay(new Date('2024-01-15 15:30:00')) // 2024-01-15 23:59:59.999
 */
export function getEndOfDay(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return endOfDay(dateObj);
}

/**
 * Checks if first date is after second date
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if date1 > date2
 * @throws Error if either date is invalid
 *
 * @example
 * isDateAfter(new Date('2024-01-20'), new Date('2024-01-15')) // true
 * isDateAfter(new Date('2024-01-15'), new Date('2024-01-20')) // false
 */
export function isDateAfter(date1: Date | string, date2: Date | string): boolean {
  const dateObj1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const dateObj2 = typeof date2 === 'string' ? new Date(date2) : date2;

  if (!isValid(dateObj1) || !isValid(dateObj2)) {
    throw new Error('Invalid dates provided');
  }

  return isAfter(dateObj1, dateObj2);
}

/**
 * Checks if first date is before second date
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if date1 < date2
 * @throws Error if either date is invalid
 *
 * @example
 * isDateBefore(new Date('2024-01-15'), new Date('2024-01-20')) // true
 * isDateBefore(new Date('2024-01-20'), new Date('2024-01-15')) // false
 */
export function isDateBefore(date1: Date | string, date2: Date | string): boolean {
  const dateObj1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const dateObj2 = typeof date2 === 'string' ? new Date(date2) : date2;

  if (!isValid(dateObj1) || !isValid(dateObj2)) {
    throw new Error('Invalid dates provided');
  }

  return isBefore(dateObj1, dateObj2);
}

/**
 * Checks if two dates are the same day (ignoring time)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if dates are on the same day
 * @throws Error if either date is invalid
 *
 * @example
 * isSameDate(new Date('2024-01-15 10:00'), new Date('2024-01-15 20:00')) // true
 * isSameDate(new Date('2024-01-15'), new Date('2024-01-16')) // false
 */
export function isSameDate(date1: Date | string, date2: Date | string): boolean {
  const dateObj1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const dateObj2 = typeof date2 === 'string' ? new Date(date2) : date2;

  if (!isValid(dateObj1) || !isValid(dateObj2)) {
    throw new Error('Invalid dates provided');
  }

  return isSameDay(dateObj1, dateObj2);
}

/**
 * Gets today's date as ISO string (YYYY-MM-DD)
 *
 * @returns Today's date in YYYY-MM-DD format
 *
 * @example
 * getTodayString() // '2024-01-15' (if today is Jan 15, 2024)
 */
export function getTodayString(): string {
  return toDateString(new Date());
}

/**
 * Converts a Date object to ISO date string (YYYY-MM-DD) in local timezone
 * This function avoids timezone conversion issues that can occur with toISOString()
 *
 * @param date - Date object or date string
 * @returns Date string in YYYY-MM-DD format (local timezone)
 *
 * @example
 * toLocalDateString(new Date()) // '2024-01-15' (local timezone)
 * toLocalDateString('2024-01-15T10:30:00') // '2024-01-15'
 */
export function toLocalDateString(date: Date | string): string {
  let dateObj: Date;

  if (typeof date === 'string') {
    // For date-only strings (YYYY-MM-DD), parse as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  // Get local date components
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Parses a Brazilian format date (dd/MM/yyyy) to Date object
 *
 * @param dateStr - Date string in dd/MM/yyyy format
 * @returns Date object
 * @throws Error if string cannot be parsed
 *
 * @example
 * parseBRDate('15/01/2024') // Date object for 2024-01-15
 */
export function parseBRDate(dateStr: string): Date {
  const parsed = parse(dateStr, 'dd/MM/yyyy', new Date());

  if (!isValid(parsed)) {
    throw new Error(`Cannot parse Brazilian date: ${dateStr}`);
  }

  return parsed;
}

export type DueStatus = 'overdue' | 'due_today' | 'upcoming' | 'paid' | 'none';

export interface DueStatusInfo {
  status: DueStatus;
  label: string;
  colorClass: string;
  bgClass: string;
  borderClass: string;
  daysUntilDue: number | null;
}

/**
 * Calculates the due date status for a transaction
 *
 * @param dueDate - The due date of the transaction
 * @param isPaid - Whether the transaction is paid
 * @returns DueStatusInfo with status, label, and styling classes
 */
export function getDueStatus(
  dueDate: Date | string | null | undefined,
  isPaid: boolean
): DueStatusInfo {
  if (isPaid) {
    return {
      status: 'paid',
      label: 'Pago',
      colorClass: 'text-green-700',
      bgClass: 'bg-green-100',
      borderClass: 'border-green-200',
      daysUntilDue: null,
    };
  }

  if (!dueDate) {
    return {
      status: 'none',
      label: 'Pendente',
      colorClass: 'text-yellow-700',
      bgClass: 'bg-[#FFF7D1]',
      borderClass: 'border-yellow-200',
      daysUntilDue: null,
    };
  }

  const today = startOfDay(new Date());
  let dueDateObj: Date;

  if (typeof dueDate === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
      dueDateObj = parse(dueDate, 'yyyy-MM-dd', new Date());
    } else {
      dueDateObj = new Date(dueDate);
    }
  } else {
    dueDateObj = dueDate;
  }

  if (!isValid(dueDateObj)) {
    return {
      status: 'none',
      label: 'Pendente',
      colorClass: 'text-yellow-700',
      bgClass: 'bg-[#FFF7D1]',
      borderClass: 'border-yellow-200',
      daysUntilDue: null,
    };
  }

  const dueDateStart = startOfDay(dueDateObj);
  const daysUntilDue = differenceInDays(dueDateStart, today);

  if (daysUntilDue < 0) {
    return {
      status: 'overdue',
      label: 'Vencido',
      colorClass: 'text-red-700',
      bgClass: 'bg-red-100',
      borderClass: 'border-red-300',
      daysUntilDue,
    };
  }

  if (daysUntilDue === 0) {
    return {
      status: 'due_today',
      label: 'Vence Hoje',
      colorClass: 'text-orange-700',
      bgClass: 'bg-orange-100',
      borderClass: 'border-orange-300',
      daysUntilDue: 0,
    };
  }

  return {
    status: 'upcoming',
    label: 'A Vencer',
    colorClass: 'text-blue-700',
    bgClass: 'bg-blue-100',
    borderClass: 'border-blue-200',
    daysUntilDue,
  };
}

const PERIOD_OPTIONS: Array<{ value: PeriodType; label: string }> = [
    { value: 'today', label: 'Hoje' },
    { value: 'thisweek', label: 'Esta semana' },
    { value: 'month', label: 'Este mês' },
    { value: 'year', label: 'Este ano' },
    { value: '30days', label: 'Últimos 30 dias' },
    { value: 'all', label: 'Todo o período' },
];

export function getDisplayedText(
  periodType: PeriodType,
  appliedDateRange: DateRange | { from?: Date; to?: Date } | undefined,
  currentDisplayDate: Date
): string {
  if (periodType === 'all') {
    return 'Todo o período';
  }

  if (appliedDateRange?.from) {
    const fromDate = appliedDateRange.from instanceof Date
      ? appliedDateRange.from
      : new Date(appliedDateRange.from);

    const dateStr = format(fromDate, "dd/MM/yyyy", { locale: ptBR });

    if (isToday(fromDate)) {
      return `Hoje - ${dateStr}`;
    }

    if (appliedDateRange?.to) {
      const toDate = appliedDateRange.to instanceof Date
        ? appliedDateRange.to
        : new Date(appliedDateRange.to);

      const fromDateStr = format(fromDate, "yyyy-MM-dd");
      const toDateStr = format(toDate, "yyyy-MM-dd");

      if (fromDateStr === toDateStr) {
        return isToday(fromDate) ? `Hoje - ${dateStr}` : dateStr;
      }
    }

    if (!appliedDateRange?.to) {
      return isToday(fromDate) ? `Hoje - ${dateStr}` : dateStr;
    }

    const selectedPeriod = PERIOD_OPTIONS.find(opt => opt.value === periodType);
    if (selectedPeriod) {
      if (periodType === 'custom' && appliedDateRange?.to) {
        const toDate = appliedDateRange.to instanceof Date
          ? appliedDateRange.to
          : new Date(appliedDateRange.to);
        const daysDiff = Math.abs((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff > 300) {
          return 'Últimos 12 meses';
        }
      }

      if (periodType === 'today') {
        return isToday(fromDate) ? `Hoje - ${dateStr}` : dateStr;
      }

      if (periodType === 'thisweek' && appliedDateRange?.to) {
        const toDate = appliedDateRange.to instanceof Date
          ? appliedDateRange.to
          : new Date(appliedDateRange.to);

        if (format(fromDate, "yyyy-MM-dd") === format(toDate, "yyyy-MM-dd")) {
          return isToday(fromDate) ? `Hoje - ${dateStr}` : dateStr;
        }

        const toDateStr = format(toDate, "dd/MM/yyyy", { locale: ptBR });
        return `${dateStr} - ${toDateStr}`;
      }

      if (periodType === 'month') {
        if (appliedDateRange?.to) {
          const toDate = appliedDateRange.to instanceof Date
            ? appliedDateRange.to
            : new Date(appliedDateRange.to);
          if (format(fromDate, "yyyy-MM-dd") === format(toDate, "yyyy-MM-dd")) {
            return isToday(fromDate) ? `Hoje - ${dateStr}` : dateStr;
          }
        }

        return format(fromDate, "MMMM 'de' yyyy", { locale: ptBR });
      }

      if (periodType === 'year') {
        if (appliedDateRange?.to) {
          const toDate = appliedDateRange.to instanceof Date
            ? appliedDateRange.to
            : new Date(appliedDateRange.to);
          if (format(fromDate, "yyyy-MM-dd") === format(toDate, "yyyy-MM-dd")) {
            return isToday(fromDate) ? `Hoje - ${dateStr}` : dateStr;
          }
        }

        return format(fromDate, "yyyy", { locale: ptBR });
      }

      if (periodType === '30days' && appliedDateRange?.to) {
        const toDate = appliedDateRange.to instanceof Date
          ? appliedDateRange.to
          : new Date(appliedDateRange.to);

        if (format(fromDate, "yyyy-MM-dd") === format(toDate, "yyyy-MM-dd")) {
          return isToday(fromDate) ? `Hoje - ${dateStr}` : dateStr;
        }

        const toDateStr = format(toDate, "dd/MM/yyyy", { locale: ptBR });
        return `${dateStr} - ${toDateStr}`;
      }
    }

    return dateStr;
  }

  const selectedPeriod = PERIOD_OPTIONS.find(opt => opt.value === periodType);
  if (selectedPeriod) {
    return selectedPeriod.label;
  }

  return format(currentDisplayDate, "dd/MM/yyyy", { locale: ptBR });
}

// ============================================================================
// ENHANCED DATE PICKER FUNCTIONALITY - BUSINESS RULES AND VALIDATION
// ============================================================================

import {
  addMonths,
  addYears,
  differenceInBusinessDays,
  isWeekend,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  getDay,
  getDate,
  getMonth,
  getYear,
  compareAsc,
  subDays,
  subYears,
  formatISO,
} from 'date-fns';

/**
 * Brazilian business rules and timezone configuration
 */
export const BRAZIL_TIMEZONE = 'America/Sao_Paulo';
export const BRAZIL_UTC_OFFSET = -3; // UTC-3

/**
 * Brazilian national holidays (static dates)
 * Note: Some holidays move based on Easter, so this should be updated annually
 */
export const BRAZILIAN_HOLIDAYS = [
  // Fixed dates
  '01-01', // New Year's Day
  '04-21', // Tiradentes Day
  '05-01', // Labor Day
  '09-07', // Independence Day
  '10-12', // Our Lady of Aparecida
  '11-02', // All Souls' Day
  '11-15', // Republic Day
  '12-25', // Christmas
];

/**
 * Date validation result interface
 */
export interface DateValidationResult {
  isValid: boolean;
  message?: string;
  suggestion?: Date;
  code?: 'FUTURE_DATE' | 'TOO_OLD' | 'WEEKEND' | 'HOLIDAY' | 'OUTSIDE_FISCAL_YEAR';
}

/**
 * Business date rules configuration
 */
export interface BusinessDateRules {
  maxFutureDays?: number; // Maximum days in future (default: 30)
  maxHistoricalYears?: number; // Maximum years in past (default: 5)
  allowWeekends?: boolean; // Allow weekend selection (default: true)
  allowHolidays?: boolean; // Allow holiday selection (default: true)
  fiscalYearStart?: number; // Fiscal year start month (default: 1 - January)
  customHolidays?: string[]; // Additional holidays in MM-DD format
}

/**
 * Converts a date to Brazil timezone (UTC-3)
 * 
 * @param date - Date to convert
 * @returns Date adjusted to Brazil timezone
 */
export function toBrazilTimezone(date: Date | string): Date {
  let dateObj: Date;

  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = new Date(date.getTime());
  }

  // Get current UTC offset and adjust to Brazil time
  const currentOffset = dateObj.getTimezoneOffset();
  const brazilOffset = BRAZIL_UTC_OFFSET * 60; // Convert hours to minutes
  const offsetDiff = currentOffset - brazilOffset;

  return new Date(dateObj.getTime() + offsetDiff * 60 * 1000);
}

/**
 * Converts a Brazil timezone date to UTC
 * 
 * @param date - Date in Brazil timezone
 * @returns Date in UTC
 */
export function fromBrazilTimezone(date: Date | string): Date {
  let dateObj: Date;

  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = new Date(date.getTime());
  }

  // Get current UTC offset and adjust from Brazil time
  const currentOffset = dateObj.getTimezoneOffset();
  const brazilOffset = BRAZIL_UTC_OFFSET * 60; // Convert hours to minutes
  const offsetDiff = brazilOffset - currentOffset;

  return new Date(dateObj.getTime() + offsetDiff * 60 * 1000);
}

/**
 * Gets current date in Brazil timezone
 * 
 * @returns Current date adjusted to Brazil timezone
 */
export function getBrazilToday(): Date {
  return toBrazilTimezone(new Date());
}

/**
 * Gets current date as ISO string in Brazil timezone
 * 
 * @returns Current date as YYYY-MM-DD in Brazil timezone
 */
export function getBrazilTodayString(): string {
  return toLocalDateString(getBrazilToday());
}

/**
 * Checks if a date is a Brazilian holiday
 * 
 * @param date - Date to check
 * @param customHolidays - Additional holidays in MM-DD format
 * @returns true if date is a holiday
 */
export function isBrazilianHoliday(date: Date | string, customHolidays: string[] = []): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Format as MM-DD for comparison
  const monthDay = format(dateObj, 'MM-dd');
  
  // Check static holidays
  if (BRAZILIAN_HOLIDAYS.includes(monthDay)) {
    return true;
  }
  
  // Check custom holidays
  if (customHolidays.includes(monthDay)) {
    return true;
  }
  
  // TODO: Add movable holidays calculation (Carnival, Easter, etc.)
  // This would require more complex calculation based on Easter
  
  return false;
}

/**
 * Checks if a date is a business day in Brazil
 * 
 * @param date - Date to check
 * @param customHolidays - Additional holidays to consider
 * @returns true if date is a business day
 */
export function isBrazilianBusinessDay(date: Date | string, customHolidays: string[] = []): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if it's weekend
  if (isWeekend(dateObj)) {
    return false;
  }
  
  // Check if it's a holiday
  if (isBrazilianHoliday(dateObj, customHolidays)) {
    return false;
  }
  
  return true;
}

/**
 * Adds business days to a date, skipping weekends and holidays
 * 
 * @param date - Starting date
 * @param businessDays - Number of business days to add
 * @param customHolidays - Additional holidays to skip
 * @returns Date after adding business days
 */
export function addBusinessDays(date: Date | string, businessDays: number, customHolidays: string[] = []): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (businessDays === 0) {
    return dateObj;
  }
  
  let result = new Date(dateObj);
  let daysToAdd = Math.abs(businessDays);
  const direction = businessDays > 0 ? 1 : -1;
  
  while (daysToAdd > 0) {
    result = addDays(result, direction);
    
    if (isBrazilianBusinessDay(result, customHolidays)) {
      daysToAdd--;
    }
  }
  
  return result;
}

/**
 * Calculates business days between two dates
 * 
 * @param startDate - Start date
 * @param endDate - End date
 * @param customHolidays - Additional holidays to exclude
 * @returns Number of business days between dates
 */
export function getBusinessDaysBetween(startDate: Date | string, endDate: Date | string, customHolidays: string[] = []): number {
  const startObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? new Date(endDate) : endDate;
  
  return differenceInBusinessDays(endObj, startObj, {
    holidays: customHolidays.map(holiday => {
      // Convert MM-DD format to full Date objects for current year
      const [month, day] = holiday.split('-').map(Number);
      const year = getYear(startObj);
      return new Date(year, month - 1, day);
    }),
  });
}

/**
 * Validates a sale date according to Brazilian business rules
 * 
 * @param date - Date to validate
 * @param rules - Business rules configuration
 * @returns ValidationResult with details
 */
export function validateSaleDate(date: Date | string, rules: BusinessDateRules = {}): DateValidationResult {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const today = getBrazilToday();
  
  // Default rules
  const defaultRules: Required<BusinessDateRules> = {
    maxFutureDays: 30,
    maxHistoricalYears: 5,
    allowWeekends: true,
    allowHolidays: true,
    fiscalYearStart: 1,
    customHolidays: [],
    ...rules,
  };
  
  // Check if date is too far in the future
  if (dateObj > today) {
    const daysDiff = differenceInDays(dateObj, today);
    if (daysDiff > defaultRules.maxFutureDays) {
      return {
        isValid: false,
        code: 'FUTURE_DATE',
        message: `Data não pode ser mais de ${defaultRules.maxFutureDays} dias no futuro`,
        suggestion: addDays(today, defaultRules.maxFutureDays),
      };
    }
  }
  
  // Check if date is too old
  const historicalLimit = subYears(today, defaultRules.maxHistoricalYears);
  if (dateObj < historicalLimit) {
    return {
      isValid: false,
      code: 'TOO_OLD',
      message: `Data não pode ser anterior a ${defaultRules.maxHistoricalYears} anos`,
      suggestion: historicalLimit,
    };
  }
  
  // Check if weekends are allowed
  if (!defaultRules.allowWeekends && isWeekend(dateObj)) {
    return {
      isValid: false,
      code: 'WEEKEND',
      message: 'Data não pode ser em um fim de semana',
      suggestion: addBusinessDays(dateObj, 1, defaultRules.customHolidays),
    };
  }
  
  // Check if holidays are allowed
  if (!defaultRules.allowHolidays && isBrazilianHoliday(dateObj, defaultRules.customHolidays)) {
    return {
      isValid: false,
      code: 'HOLIDAY',
      message: 'Data não pode ser em um feriado',
      suggestion: addBusinessDays(dateObj, 1, defaultRules.customHolidays),
    };
  }
  
  // Check fiscal year boundaries
  const currentYear = getYear(today);
  const dateYear = getYear(dateObj);
  
  if (dateYear < currentYear - 1 || dateYear > currentYear + 1) {
    return {
      isValid: false,
      code: 'OUTSIDE_FISCAL_YEAR',
      message: 'Data deve estar dentro do ano fiscal atual ou adjacente',
      suggestion: new Date(currentYear, getMonth(today), today.getDate()),
    };
  }
  
  return {
    isValid: true,
  };
}

/**
 * Calculates expiry date for budget based on payment terms
 * 
 * @param saleDate - Base sale date
 * @param paymentTerms - Payment terms string (e.g., '7_days', '30_days', 'immediate')
 * @param businessDaysOnly - Calculate using business days only
 * @param customHolidays - Custom holidays for business day calculation
 * @returns Calculated expiry date
 */
export function calculateExpiryDate(
  saleDate: Date | string,
  paymentTerms: string,
  businessDaysOnly: boolean = true,
  customHolidays: string[] = []
): Date {
  const saleDateObj = typeof saleDate === 'string' ? new Date(saleDate) : saleDate;
  
  // Default to 7 days if payment terms is not specified
  if (!paymentTerms || paymentTerms === 'immediate') {
    return businessDaysOnly 
      ? addBusinessDays(saleDateObj, 7, customHolidays)
      : addDays(saleDateObj, 7);
  }
  
  // Parse payment terms
  const termsMap: Record<string, number> = {
    '7_days': 7,
    '15_days': 15,
    '30_days': 30,
    '45_days': 45,
    '60_days': 60,
    'custom': 7, // Default for custom terms
  };
  
  const days = termsMap[paymentTerms] || 7;
  
  // Ensure minimum 7 days for budgets
  const finalDays = Math.max(days, 7);
  
  return businessDaysOnly 
    ? addBusinessDays(saleDateObj, finalDays, customHolidays)
    : addDays(saleDateObj, finalDays);
}

/**
 * Formats date with timezone information for display
 * 
 * @param date - Date to format
 * @param timezone - Target timezone (default: Brazil)
 * @returns Formatted date string with timezone indicator
 */
export function formatDateWithTimezone(date: Date | string, timezone: string = BRAZIL_TIMEZONE): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (timezone === BRAZIL_TIMEZONE) {
    const brazilDate = toBrazilTimezone(dateObj);
    return `${format(brazilDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })} (BRT)`;
  }
  
  return `${format(dateObj, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })} (${timezone})`;
}

/**
 * Checks if a date range contains only business days
 * 
 * @param startDate - Start date
 * @param endDate - End date
 * @param customHolidays - Additional holidays to consider
 * @returns true if all days in range are business days
 */
export function isBusinessDateRange(startDate: Date | string, endDate: Date | string, customHolidays: string[] = []): boolean {
  const startObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? new Date(endDate) : endDate;
  
  let currentDate = new Date(startObj);
  
  while (currentDate <= endObj) {
    if (!isBrazilianBusinessDay(currentDate, customHolidays)) {
      return false;
    }
    currentDate = addDays(currentDate, 1);
  }
  
  return true;
}

/**
 * Gets the next business day after a given date
 * 
 * @param date - Starting date
 * @param customHolidays - Additional holidays to consider
 * @returns Next business day
 */
export function getNextBusinessDay(date: Date | string, customHolidays: string[] = []): Date {
  return addBusinessDays(date, 1, customHolidays);
}

/**
 * Gets the previous business day before a given date
 * 
 * @param date - Starting date
 * @param customHolidays - Additional holidays to consider
 * @returns Previous business day
 */
export function getPreviousBusinessDay(date: Date | string, customHolidays: string[] = []): Date {
  return addBusinessDays(date, -1, customHolidays);
}

/**
 * Quick date selection options for the date picker
 */
export interface QuickDateOption {
  id: string;
  label: string;
  getDate: () => Date;
  isAvailable: () => boolean;
}

/**
 * Gets available quick date options
 * 
 * @param rules - Business rules to apply
 * @returns Array of quick date options
 */
export function getQuickDateOptions(rules: BusinessDateRules = {}): QuickDateOption[] {
  const today = getBrazilToday();
  const validationRules = { ...rules };
  
  return [
    {
      id: 'today',
      label: 'Hoje',
      getDate: () => today,
      isAvailable: () => validateSaleDate(today, validationRules).isValid,
    },
    {
      id: 'yesterday',
      label: 'Ontem',
      getDate: () => subDays(today, 1),
      isAvailable: () => validateSaleDate(subDays(today, 1), validationRules).isValid,
    },
    {
      id: 'nextWeek',
      label: 'Próxima Semana',
      getDate: () => addDays(today, 7),
      isAvailable: () => validateSaleDate(addDays(today, 7), validationRules).isValid,
    },
    {
      id: 'nextMonth',
      label: 'Próximo Mês',
      getDate: () => addMonths(today, 1),
      isAvailable: () => validateSaleDate(addMonths(today, 1), validationRules).isValid,
    },
    {
      id: 'lastBusinessDay',
      label: 'Último Dia Útil',
      getDate: () => getPreviousBusinessDay(today),
      isAvailable: () => validateSaleDate(getPreviousBusinessDay(today), validationRules).isValid,
    },
    {
      id: 'nextBusinessDay',
      label: 'Próximo Dia Útil',
      getDate: () => getNextBusinessDay(today),
      isAvailable: () => validateSaleDate(getNextBusinessDay(today), validationRules).isValid,
    },
  ];
}